import React, { useEffect, useId, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import type { ElegibleReparto, RepartoElegibles } from '../types/metas.types';
import { pesos, pesosEnteros } from '../utils/formato';
import { parteIgualAproximada } from '../utils/reparto';
import { CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_BOTON_TEXTO, CLASE_OVERLAY, cx } from './ui/clases';
import { Pill } from './ui/Pill';

interface Props {
  /** Quién puede recibir (paso 1 de la API); `null` = cerrado. */
  datos: RepartoElegibles | null;
  /**
   * Lo que se marca al abrir. `null`/ausente = la preselección de la API; con
   * «Cambiar selección» (desde la propuesta), lo que se había elegido.
   */
  inicial?: string[] | null;
  /** «Ver reparto»: las llaves marcadas, en el orden de la captura. */
  onVerReparto: (keys: string[]) => void;
  /** «Cancelar», la ✕ o Escape: no cambia nada. */
  onHide: () => void;
  /** Teléfono: a pantalla completa, con renglones y botones de 44 px. */
  movil?: boolean;
  /** Pidiendo la propuesta (paso 2). */
  ocupado?: boolean;
  /** Por qué la API no la propuso (422: poco por asignar, alguien ya no puede recibir…). */
  error?: string | null;
}

/** Lo que dice cada aviso junto al nombre (contrato de la ronda F, §1.3). */
const AVISO: Record<NonNullable<ElegibleReparto['aviso']>, { chip: string; texto: string }> = {
  bodeguero: {
    chip: 'Bodeguero',
    texto: 'Se le pondrá meta al bodeguero: sale el aviso «Se le está colocando meta al bodeguero»',
  },
  casa: { chip: 'Casa', texto: 'Casa de la tienda: cuenta para la meta, nadie la cobra' },
  // «(pregunta abierta)» vive en la spec (§4.9, pregunta 7), no ante el supervisor (26-oct-2026).
  gerente: { chip: 'Gerente', texto: 'Es gerente: su meta es opcional y se calcula como vendedor' },
};

/** La preselección de la API, o lo que venía marcado (sólo lo que sigue siendo elegible). */
function marcadasDe(datos: RepartoElegibles | null, inicial: string[] | null | undefined): Set<string> {
  if (!datos) return new Set();
  if (inicial) {
    const validas = new Set(datos.elegibles.map((e) => e.key));
    return new Set(inicial.filter((k) => validas.has(k)));
  }
  return new Set(datos.elegibles.filter((e) => e.preseleccionado).map((e) => e.key));
}

/**
 * PASO 1 de «Repartir en partes iguales» (ronda F, 23-oct-2026, decisión del
 * usuario: «una vez seleccionado el botón, nos pedirá seleccionar entre quienes
 * se va a repartir en partes iguales (no te pido solo vendedores por los casos
 * donde el bodeguero o casa juegan en la meta)»).
 *
 * Una casilla por quien puede recibir, con la preselección de la API (los
 * vendedores y la Casa; el gerente y el bodeguero, sólo si ya tienen meta), y
 * en gris quien no puede, con su razón. Al pie, lo que tocaría a cada uno
 * (estimado: la cifra buena, en múltiplos de $100 y con el residuo a quien más
 * vende, la da la API en el paso 2). Nada se guarda aquí.
 */
export const ElegirRepartoDialog: React.FC<Props> = ({ datos, inicial, onVerReparto, onHide, movil = false, ocupado = false, error }) => {
  const [marcadas, setMarcadas] = useState<Set<string>>(() => marcadasDe(datos, inicial));
  // Cada apertura (o «Cambiar selección») empieza con lo suyo.
  useEffect(() => {
    setMarcadas(marcadasDe(datos, inicial));
  }, [datos, inicial]);
  const idLeyenda = useId();

  const elegibles = datos?.elegibles ?? [];
  const elegidas = elegibles.filter((e) => marcadas.has(e.key)).map((e) => e.key);
  const n = elegidas.length;
  const parte = datos ? parteIgualAproximada(datos.porAsignar, n) : null;

  const alternar = (k: string, si: boolean) =>
    setMarcadas((m) => {
      const nueva = new Set(m);
      if (si) nueva.add(k);
      else nueva.delete(k);
      return nueva;
    });

  const pie = (
    <div className={cx('flex gap-2', movil ? 'flex-col-reverse' : 'items-center justify-end')}>
      <button type="button" className={cx(CLASE_BOTON, movil && 'w-full')} onClick={onHide}>
        Cancelar
      </button>
      <button
        type="button"
        className={cx(CLASE_BOTON_PRIMARIO, movil && 'w-full')}
        onClick={() => onVerReparto(elegidas)}
        disabled={!n || ocupado}
        aria-busy={ocupado || undefined}
      >
        {ocupado && <i className="pi pi-spin pi-spinner" aria-hidden="true" />}
        Ver reparto
      </button>
    </div>
  );

  // En pantalla táctil (y en el teléfono) el renglón entero es el blanco, de 44 px.
  const renglon = cx(
    'flex cursor-pointer items-start gap-3 rounded-metas-control border border-metas-borde-suave px-3 py-2.5 hover:bg-metas-cabecera',
    movil ? 'min-h-metas-tactil' : '[@media(pointer:coarse)]:min-h-metas-tactil',
  );
  const casilla = cx(
    'mt-0.5 shrink-0 accent-metas-primario',
    movil ? 'h-5 w-5' : 'h-[18px] w-[18px] [@media(pointer:coarse)]:h-5 [@media(pointer:coarse)]:w-5',
  );
  const textoEnlace = cx(CLASE_BOTON_TEXTO, '!h-auto px-1 py-0 text-[13px]', movil ? 'min-h-metas-tactil' : '[@media(pointer:coarse)]:min-h-metas-tactil');

  return (
    <Dialog
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header="Repartir en partes iguales"
      visible={!!datos}
      onHide={onHide}
      footer={pie}
      maximized={movil}
      onMaximize={() => undefined}
      style={movil ? { width: '100vw', maxWidth: '100vw', margin: 0 } : { width: '95vw', maxWidth: 640 }}
      modal
    >
      {datos && (
        <div className="flex flex-col gap-4 text-sm text-metas-texto" data-testid="elegir-reparto">
          <p className="m-0 text-metas-texto-2">
            Faltan <b className="num font-semibold text-metas-texto">{pesos(datos.porAsignar)}</b> para la meta de tienda (
            <span className="num">{pesos(datos.metaTienda)}</span>). Elige entre quiénes se reparte: a cada uno se le suma la misma
            parte, en múltiplos de {pesos(datos.multiplo)}; la diferencia del redondeo la absorbe quien más vende.
          </p>

          <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0" aria-labelledby={idLeyenda}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span id={idLeyenda} className="text-metas-etiqueta uppercase text-metas-texto-2">
                Entre quiénes se reparte ({n} de {elegibles.length})
              </span>
              <span className="flex gap-1">
                <button
                  type="button"
                  className={textoEnlace}
                  onClick={() => setMarcadas(new Set(elegibles.map((e) => e.key)))}
                  disabled={n === elegibles.length}
                >
                  Todos
                </button>
                <button type="button" className={textoEnlace} onClick={() => setMarcadas(new Set())} disabled={!n}>
                  Ninguno
                </button>
              </span>
            </div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {elegibles.map((e) => {
                const aviso = e.aviso ? AVISO[e.aviso] : null;
                return (
                  <li key={e.key} data-testid="elegible-reparto">
                    <label className={renglon}>
                      <input
                        type="checkbox"
                        className={casilla}
                        checked={marcadas.has(e.key)}
                        onChange={(ev) => alternar(e.key, ev.target.checked)}
                        aria-label={`Repartir a ${e.nombre}`}
                      />
                      <span className="flex min-w-0 flex-grow flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-semibold [overflow-wrap:anywhere]">{e.nombre}</span>
                          {aviso && (
                            <Pill tono={e.aviso === 'casa' ? 'neutro' : 'aviso'} className="!h-5 !px-2 !text-[11px]">
                              {aviso.chip}
                            </Pill>
                          )}
                        </span>
                        <span className="num text-xs text-metas-texto-2">
                          {[e.puesto && e.puesto.trim() ? e.puesto.trim() : null, e.metaActual !== null ? `Meta actual ${pesosEnteros(e.metaActual)}` : 'Sin meta']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        {aviso && <span className="text-xs text-metas-aviso [overflow-wrap:anywhere]">{aviso.texto}</span>}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>

          {datos.omitidas.length > 0 && (
            <div className="flex flex-col gap-1.5" data-testid="elegir-omitidas">
              <span className="text-metas-etiqueta uppercase text-metas-texto-2">No pueden recibir ({datos.omitidas.length})</span>
              <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-metas-texto-2">
                {datos.omitidas.map((o) => (
                  <li key={o.key} className="[overflow-wrap:anywhere]">
                    <b className="font-semibold">{o.nombre}</b>: {o.razon}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div
            role="status"
            className={cx(
              'rounded-metas-control px-3 py-2 text-[13px] font-semibold',
              n ? 'bg-metas-info-fondo text-metas-info' : 'bg-metas-neutro-fondo text-metas-texto-2',
            )}
            data-testid="elegir-estimado"
          >
            {n && parte !== null
              ? `≈ ${pesosEnteros(parte)} a cada uno (${n === 1 ? '1 persona' : `${n} personas`})`
              : 'Elige al menos a una persona'}
          </div>

          {error && (
            <div role="alert" className="rounded-metas-control border border-metas-error bg-metas-error-fondo px-3 py-2 text-[13px] text-metas-error [overflow-wrap:anywhere]">
              {error}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default ElegirRepartoDialog;
